import { useMemo } from 'react';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { JsonSchema } from '@sagold/react-json-editor';

import {
  METADATA_EXTRACTION_FROM_AI_VALUE,
  METADATA_EXTRACTION_FROM_FILE_NAME_VALUE,
} from '../constants';
import {
  allOfEntryTargetsAnyKey,
  collectStep2Properties,
} from '../step2SchemaProperties';

export type GeneratedSchemaFromMetaDataExtraction = {
  jsonSchemaWithAIExtraction?: JsonSchema;
  jsonSchemaWithFileNameExtraction?: JsonSchema;
};

/**
 * Given a set of property keys present in a step-2 schema and the original
 * schema's allOf array, returns the matching allOf entries and any hidden
 * dependency properties required by their `if` clauses.
 */
function collectAllOfAndHiddenDeps(
  allOf: JsonSchema['allOf'],
  step2Keys: Set<string>,
  originalProperties: NonNullable<JsonSchema['properties']>
): {
  filteredAllOf: NonNullable<JsonSchema['allOf']>;
  hiddenDependencyProps: NonNullable<JsonSchema['properties']>;
} {
  const filteredAllOf: NonNullable<JsonSchema['allOf']> = [];
  const hiddenDependencyProps: NonNullable<JsonSchema['properties']> = {};

  if (!allOf) {
    return { filteredAllOf, hiddenDependencyProps };
  }

  for (const entry of allOf) {
    if (!allOfEntryTargetsAnyKey(entry, step2Keys)) {
      continue;
    }

    filteredAllOf.push(entry);

    const ifProps =
      typeof entry.if === 'object' ? entry.if.properties : undefined;
    if (!ifProps) {
      continue;
    }

    for (const depKey of Object.keys(ifProps)) {
      if (step2Keys.has(depKey) || hiddenDependencyProps[depKey]) {
        continue;
      }
      const originalProp = originalProperties[depKey];
      if (typeof originalProp !== 'object') {
        continue;
      }
      hiddenDependencyProps[depKey] = {
        ...originalProp,
        options: { ...originalProp.options, hidden: true, disabled: true },
      };
    }
  }

  return { filteredAllOf, hiddenDependencyProps };
}

export const useGenerateSchemaForMetaDataExtraction = ({
  fileNameMetadataExtractionConfig,
  jsonSchema,
}: {
  fileNameMetadataExtractionConfig?: FileUploadMetaDataExtractionDto;
  jsonSchema: JsonSchema;
}): GeneratedSchemaFromMetaDataExtraction => {
  return useMemo(() => {
    const clonedSchema = structuredClone(jsonSchema);
    const result: GeneratedSchemaFromMetaDataExtraction = {};

    const {
      properties: aiOrUserProperties,
      requiredKeys: aiOrUserRequiredKeys,
    } = collectStep2Properties(clonedSchema, {
      extractionMode: METADATA_EXTRACTION_FROM_AI_VALUE,
    });

    if (Object.keys(aiOrUserProperties).length > 0) {
      const aiStep2Keys = new Set(Object.keys(aiOrUserProperties));
      const { filteredAllOf: aiAllOf, hiddenDependencyProps: aiHiddenDeps } =
        collectAllOfAndHiddenDeps(
          clonedSchema.allOf,
          aiStep2Keys,
          clonedSchema.properties ?? {}
        );

      result.jsonSchemaWithAIExtraction = {
        format: 'column',
        type: clonedSchema.type,
        required:
          aiOrUserRequiredKeys.length > 0 ? aiOrUserRequiredKeys : undefined,
        properties: { ...aiOrUserProperties, ...aiHiddenDeps },
        ...(aiAllOf.length > 0 && { allOf: aiAllOf }),
      };
    }

    // generate schema for fileName extraction method
    if (fileNameMetadataExtractionConfig?.key) {
      const keyToRetain = fileNameMetadataExtractionConfig.key;
      const propertyToModify = clonedSchema.properties?.[keyToRetain];

      if (
        propertyToModify &&
        typeof propertyToModify === 'object' &&
        typeof propertyToModify.conditionalFilter?.dataProperty === 'string'
      ) {
        const {
          properties: fileNameProps,
          requiredKeys: fileNameRequiredKeys,
        } = collectStep2Properties(clonedSchema, {
          extractionMode: METADATA_EXTRACTION_FROM_FILE_NAME_VALUE,
          fileNameMetadataExtractionKey: keyToRetain,
        });
        const fileNameStep2Keys = new Set(Object.keys(fileNameProps));
        const { filteredAllOf: fnAllOf, hiddenDependencyProps: fnHiddenDeps } =
          collectAllOfAndHiddenDeps(
            clonedSchema.allOf,
            fileNameStep2Keys,
            clonedSchema.properties ?? {}
          );

        result.jsonSchemaWithFileNameExtraction = {
          type: clonedSchema.type,
          required:
            fileNameRequiredKeys.length > 0 ? fileNameRequiredKeys : undefined,
          properties: { ...fileNameProps, ...fnHiddenDeps },
          ...(fnAllOf.length > 0 && { allOf: fnAllOf }),
        };
      }
    }

    return result;
  }, [fileNameMetadataExtractionConfig?.key, jsonSchema]);
};
